import { SpriteIcon } from "@components/core/icons/SpriteIcon";
import { AddToCart, AddToPlaylist, Tooltip } from "@components/interaction";
import { ItemLoader } from "@components/loaders";
import { useMediaQuery } from "@lib/hooks/useMediaQuery";
import { Track } from "@models/track";
import { device } from "@styles/theme";
import { useTranslation } from "next-i18next";
import {
	LoadWrapper,
	PlayControlWrapper,
	PlayControls,
	TrackControlsWrapper,
	VolumeControls,
	Wrapper,
} from "./Controls.style";
import { Fader } from "./fader";

interface Props {
	track?: Track;
	volume: number;
	isVolumeFaderVisible: boolean;
	isPlaying: boolean;
	isLoading: boolean;
	playlistVisible?: boolean;
	onKeyboardShortcutsClick: () => void;
	onVolumeClick: () => void;
	onVolumeChange: (val: number) => void;
	onPlayPreviousTrackClick: () => void;
	onPlayCurrentTrackClick: () => void;
	onPauseCurrentTrackClick: () => void;
	onPlayNextTrackClick: () => void;
	onTogglePlaylistClick: () => void;
}

const Controls: React.FC<Props> = ({
	track,
	volume,
	isVolumeFaderVisible,
	isPlaying,
	isLoading,
	playlistVisible,
	onKeyboardShortcutsClick,
	onVolumeClick,
	onVolumeChange,
	onPlayPreviousTrackClick,
	onPlayCurrentTrackClick,
	onPauseCurrentTrackClick,
	onPlayNextTrackClick,
	onTogglePlaylistClick,
}) => {
	const { t } = useTranslation();
	const isLG = useMediaQuery({ query: device.lg });
	const isMD = useMediaQuery({ query: device.sm });
	return (
		<Wrapper className="controls">
			{track && (
				<>
					<AddToPlaylist tracks={[track]} />
					<div><AddToCart location="Player" iconOnly={!isMD} track={track} /></div>
				</>
			)}
			<PlayControls>
				{isLG && (
					<>
						<Tooltip text={t("Player.KeyboardControls")} position="top">
							<div
								onClick={(e) => {
									e.preventDefault();
									onKeyboardShortcutsClick();
								}}
							>
								<SpriteIcon id="keyboard" width="24" height="24" />
							</div>
						</Tooltip>
						<VolumeControls>
							<Tooltip text={t("Player.Volume")} position="top">
								<div
									onClick={(e) => {
										e.preventDefault();
										onVolumeClick();
									}}
								>
									{volume === 0 ?
											(
												<SpriteIcon id="volume-mute" width="24" height="24" />
											) :
										volume > 0 && volume < 0.5 ?
												(
													<SpriteIcon id="volume-low" width="24" height="24" />
												) :
												(
													<SpriteIcon id="volume" width="24" height="24" />
												)}
								</div>
							</Tooltip>
							{isVolumeFaderVisible && (
								<Fader
									value={volume}
									min={0}
									max={1}
									step={0.01}
									onValueChange={onVolumeChange}
								/>
							)}
						</VolumeControls>
					</>
				)}
				<TrackControlsWrapper>
					<div
						onClick={(e) => {
							e.preventDefault();
							onPlayPreviousTrackClick();
						}}
					>
						<SpriteIcon id="track-previous" height="1.5rem" width="1.5rem" />
					</div>
					<PlayControlWrapper>
						{
							isLoading ?
									(
										<LoadWrapper>
											<ItemLoader />
										</LoadWrapper>
									) :
									(
										<>
											{isPlaying ?
													(
														<div
															onClick={(e) => {
																e.preventDefault();
																onPauseCurrentTrackClick();
															}}
														>
															<SpriteIcon id="track-pause" height="1.5rem" width="1.5rem" data-testid="player-control-pause_track" />
														</div>
													) :
													(
														<div
															onClick={(e) => {
																e.preventDefault();
																onPlayCurrentTrackClick();
															}}
														>
															<SpriteIcon id="track-play" height="1.5rem" width="1.5rem" data-testid="player-control-play_track" />
														</div>
													)}
										</>
									)
						}
					</PlayControlWrapper>
					<div

						onClick={(e) => {
							e.preventDefault();
							onPlayNextTrackClick();
						}}
					>
						<SpriteIcon id="track-next" height="1.5rem" width="1.5rem" />
					</div>
				</TrackControlsWrapper>
				<Tooltip text={playlistVisible ? t("Player.CloseQueue") : t("Player.OpenQueue")} position="top-left">
					<div
						onClick={(e) => {
							e.preventDefault();
							onTogglePlaylistClick();
						}}
					>
						<SpriteIcon id={playlistVisible ? "player-queue-open" : "player-queue-closed"} width="24" height="24" />
					</div>
				</Tooltip>
			</PlayControls>
		</Wrapper>
	);
};

export default Controls;
